import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { MailOverview } from '@adornis/mail-client/mail-overview.js';
import { html, nothing } from 'lit';
import { archiveMails } from '../../api/archiveMails.js';
import { getMailSyncStatus } from '../../api/getMailSyncStatus.js';
import { subscribeEnrichedMailEntitiesPaginatedFiltered } from '../../api/subscribeEnrichedMailEntitiesPaginatedFiltered.js';
import { triggerMailSync } from '../../api/triggerMailSync.js';
import { unarchiveMails } from '../../api/unarchiveMails.js';
import { EnrichedMailEntity } from '../../db/EnrichedMailEntity.js';
import { EVALUATION_OPTIONS, renderMailFilterPanel, useMailboxFilter } from './MailFilterPanel.js';

// Compact, single-column master list for the master–detail layout: one tappable row per
// request showing subject, sender, a single status chip and the date. Everything that used
// to be its own table column is folded into the row so the list stays narrow and scannable.
const makeMailColumn = (
  selectedMailID: string | null | undefined,
  selectedIDs: string[],
  onSelect: (index: number, id: string, shift: boolean) => void,
  registerRow: (index: number, id: string, archived: boolean) => void,
): ColumnDefinition<typeof EnrichedMailEntity> => ({
  customID: 'mail',
  columnName: 'Anfragen',
  columnWidth: '1fr',
  isSortable: false,
  selectionSet: {
    _id: 1,
    subject: 1,
    textContent: 1,
    attachments: 1,
    receivedAt: 1,
    archivedAt: 1,
    from: { address: 1, name: 1 },
    mailProcessings: {
      sentToSap: 1,
      sapNumber: 1,
      evaluation: 1,
      sapValidation: { ok: 1 },
      extractionResult: { requestID: 1 },
    },
  },
  renderItem: (mail: EnrichedMailEntity, _viewElement, indexRow: number) => {
    // Track the rendered row order (for Shift-click ranges) and each row's archive
    // state (so the bulk bar can offer only the actions that fit the selection).
    registerRow(indexRow, mail._id, !!mail.archivedAt);
    const proc = mail.mailProcessings?.[0];
    const sender = Array.isArray(mail.from) ? mail.from[0] : undefined;
    const senderName = sender?.name || sender?.address || '—';
    const date = mail.receivedAt
      ? `${mail.receivedAt.toLocaleDateString()}, ${mail.receivedAt.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}`
      : '';
    // The open request is highlighted with the single accent so the list reads as the
    // master pane of a master–detail view (mail._id is the route's mailID).
    const isActive = !!selectedMailID && mail._id === selectedMailID;
    const isChecked = selectedIDs.includes(mail._id);
    const isArchived = !!mail.archivedAt;
    // Evaluation rating (if rated) — shown as a coloured dot so the rating is
    // scannable in the list; not-evaluated rows simply have no dot.
    const evalOption = proc?.evaluation ? EVALUATION_OPTIONS.find(o => o.value === proc.evaluation) : undefined;

    // Business identifiers that used to live in their own table columns — kept here as a
    // compact muted meta line so the list stays scannable but loses no information.
    const requestID = proc?.extractionResult?.requestID;
    const sapNumber = proc?.sapNumber;

    // Status folds the former "SAP-Status" + "Validierung" columns into one quiet chip:
    // sent → done, missing mandatory fields → error, validated-but-unsent → ready, else open.
    const status = proc?.sentToSap?.length
      ? { label: 'gesendet', dot: 'bg-base-content/30' }
      : proc?.sapValidation && !proc.sapValidation.ok
        ? { label: 'Pflichtfelder fehlen', dot: 'bg-error' }
        : proc?.sapValidation?.ok
          ? { label: 'bereit', dot: 'bg-base-content/30' }
          : { label: 'offen', dot: 'bg-base-content/15' };

    // Archived rows get an amber tint so they stand out when archived mails are
    // shown alongside active ones. Active (open) row keeps priority, then the
    // archived cue, then the selection highlight.
    const rowBg = isActive
      ? 'bg-primary/10'
      : isArchived
        ? 'bg-amber-100/70 hover:bg-amber-100'
        : isChecked
          ? 'bg-base-200/70'
          : 'hover:bg-base-200/60';

    return html`
      <div class="flex items-start gap-2 w-full min-w-0 rounded-lg px-3 py-2.5 transition-colors ${rowBg}">
        <!-- Selection checkbox: stops propagation so ticking a row never opens it
             (the surrounding row is a navigation link in the master–detail view).
             Controlled via .checked + preventDefault so Shift-click can drive a
             range selection without the browser's own toggle fighting it. -->
        <input
          type="checkbox"
          class="checkbox checkbox-sm checkbox-primary rounded-full shrink-0 mt-0.5"
          .checked=${isChecked}
          title="Auswählen (Shift+Klick wählt den Bereich)"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            onSelect(indexRow, mail._id, e.shiftKey);
          }}
        />
        <div class="flex flex-col gap-1 min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <span
              class="text-sm leading-snug line-clamp-2 min-w-0 font-medium ${isActive
                ? 'text-primary'
                : 'text-base-content'}"
              >${mail.subject}</span
            >
            <div class="flex items-center gap-1 shrink-0">
              ${evalOption
                ? html`<span
                    class="h-2 w-2 rounded-full"
                    style="background-color:${evalOption.dot}"
                    title="Bewertung: ${evalOption.label}"
                  ></span>`
                : nothing}
              ${mail.attachments?.length
                ? html`<x-icon class="text-sm text-base-content/30" title="Anhang">attach_file</x-icon>`
                : nothing}
            </div>
          </div>
          <div class="flex items-center justify-between gap-2 text-xs">
            <span class="truncate text-base-content/55">${senderName}</span>
            <span class="shrink-0 text-base-content/40">${date}</span>
          </div>
          <div class="flex items-center justify-between gap-2 text-xs text-base-content/40">
            <span class="flex items-center gap-1.5 min-w-0">
              ${isArchived
                ? html`<span
                    class="shrink-0 rounded bg-amber-200 text-amber-900 px-1 py-0.5 text-[10px] font-semibold leading-none"
                    >Archiviert</span
                  >`
                : nothing}
              <span class="w-1.5 h-1.5 rounded-full shrink-0 ${status.dot}"></span>
              <span class="truncate">${status.label}</span>
            </span>
            ${requestID || sapNumber
              ? html`<span class="shrink-0 font-mono tabular-nums text-base-content/40">
                  ${sapNumber ? html`SAP ${sapNumber}` : html`Anfr. ${requestID}`}
                </span>`
              : nothing}
          </div>
        </div>
      </div>
    `;
  },
});

// Selection bar that replaces the filter header while rows are selected: a bold
// count + "Aufheben" link over full-width action buttons. The actions adapt to the
// selection — "Archivieren" only when something not-yet-archived is selected,
// "Wiederherstellen" only when something archived is selected.
const renderBulkBar = ({
  count,
  canArchive,
  canUnarchive,
  onClear,
  onArchive,
  onUnarchive,
}: {
  count: number;
  canArchive: boolean;
  canUnarchive: boolean;
  onClear: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
}) => html`
  <div class="px-3.5 pt-4 pb-3 border-b border-base-200 bg-base-200/30 shrink-0">
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm font-semibold text-base-content">${count} ausgewählt</span>
      <button class="text-xs font-medium text-primary hover:underline" @click=${onClear}>Aufheben</button>
    </div>
    <div class="flex items-center gap-2 mt-3">
      ${canUnarchive
        ? html`<button class="btn btn-outline flex-1 min-w-0 gap-1.5 font-semibold" @click=${onUnarchive}>
            <x-icon class="text-base shrink-0">unarchive</x-icon> <span class="truncate">Wiederherstellen</span>
          </button>`
        : nothing}
      ${canArchive
        ? html`<button class="btn btn-primary flex-1 min-w-0 gap-1.5 font-semibold" @click=${onArchive}>
            <x-icon class="text-base shrink-0">archive</x-icon> <span class="truncate">Archivieren</span>
          </button>`
        : nothing}
    </div>
  </div>
`;

export const CustomMailOverview = stateful((props: { selectedMailID?: string | null }) => {
  // All filter state, handlers and panel render-props live in the hook; the panel is
  // rendered from `panel` and the live `filter` drives the list query below.
  const { filter, panel } = useMailboxFilter();

  // Bulk selection: tick checkboxes in the list, then archive all at once. Kept in
  // local state (outside the filter/view) so toggling never re-queries the list.
  const [selectedIDs, setSelectedIDs] = useState<string[]>([], []);
  // Rendered row order (index → id), filled as rows render, so Shift-click can
  // resolve the range between the anchor and the clicked row. `lastIndex` is the
  // selection anchor (the previously clicked row).
  const [rowOrder] = useState(new Map<number, string>(), []);
  // id → archived, so the bulk bar can show only the actions that fit the selection.
  const [rowArchived] = useState(new Map<string, boolean>(), []);
  const [lastIndex, setLastIndex] = useState<number | null>(null, []);
  const registerRow = (index: number, id: string, archived: boolean) => {
    rowOrder.set(index, id);
    rowArchived.set(id, archived);
  };

  const handleSelect = (index: number, id: string, shift: boolean) => {
    if (shift && lastIndex !== null) {
      // Shift-click: add every row between the anchor and the clicked row.
      const lo = Math.min(lastIndex, index);
      const hi = Math.max(lastIndex, index);
      const set = new Set(selectedIDs);
      for (let i = lo; i <= hi; i += 1) {
        const rid = rowOrder.get(i);
        if (rid) set.add(rid);
      }
      setSelectedIDs([...set]);
    } else {
      setSelectedIDs(selectedIDs.includes(id) ? selectedIDs.filter(x => x !== id) : [...selectedIDs, id]);
    }
    setLastIndex(index);
  };

  const clearSelection = () => {
    setSelectedIDs([]);
    setLastIndex(null);
  };

  // Archive and restore share the same flow (confirm dialog → mutation → snackbar →
  // clear selection); only the operation and wording differ.
  const runBulk = (op: (ids: string[]) => () => Promise<number>, busy: string, done: string) => {
    const ids = [...selectedIDs];
    if (!ids.length) return;
    void XNativeDialog.waitFor(op(ids)(), `${ids.length} ${busy}`)
      .then(count => {
        clearSelection();
        return XSnackbar.show(`${count} ${done}`);
      })
      .catch(err => XSnackbar.show('Fehler: ' + err.message));
  };
  const handleBulkArchive = () => runBulk(archiveMails, 'Anfrage(n) werden archiviert…', 'Anfrage(n) archiviert.');
  const handleBulkUnarchive = () =>
    runBulk(unarchiveMails, 'Anfrage(n) werden wiederhergestellt…', 'Anfrage(n) aus dem Archiv geholt.');

  // Manual refresh: pull new mail from the server now instead of waiting for the polling
  // interval. The list updates reactively via its subscription once the fetch completes.
  // A 5→0 second countdown inside the button throttles clicks (anti-spam); `null` means idle.
  const [refreshing, setRefreshing] = useState(false);
  const [cooldown, setCooldown] = useState<number | null>(null);
  useEffect(() => {
    if (cooldown === null) return;
    const id = setTimeout(() => setCooldown(cooldown <= 0 ? null : cooldown - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const [syncVersion, setSyncVersion] = useState(0);
  // Poll the status as well: a frozen "abgerufen vor 2 min" on an open tab would keep claiming
  // the mailbox is healthy long after the poll died.
  useEffect(() => {
    const id = setInterval(() => setSyncVersion(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const { value: syncStatus } = useQuery(
    () => getMailSyncStatus()({ _id: 1, runStartedAt: 1, lastSuccessAt: 1, lastError: 1, stalledSince: 1 }),
    [syncVersion],
  );

  const handleRefresh = () => {
    if (cooldown !== null || refreshing) return;
    setRefreshing(true);
    setCooldown(5);
    void triggerMailSync()()
      .then(started => {
        // `false` means the poll is not running at all. Without this the button just spins and
        // the mailbox stays stale without anyone being told.
        if (!started) XSnackbar.show('Der Postfach-Abruf läuft nicht — bitte den Dienst prüfen.');
        setSyncVersion(Date.now());
      })
      .catch((err: Error) => XSnackbar.show('Aktualisieren fehlgeschlagen: ' + err.message))
      .finally(() => setRefreshing(false));
  };

  return html`
    <div class="flex flex-col h-full min-h-0">
      <!-- Selection mode takes over the header: while rows are checked the filter
           panel is hidden and the bulk action bar shows in its place. -->
      ${selectedIDs.length
        ? renderBulkBar({
            count: selectedIDs.length,
            // Unknown rows (never rendered) count as not-archived, so the default action stays "Archivieren".
            canUnarchive: selectedIDs.some(id => rowArchived.get(id)),
            canArchive: selectedIDs.some(id => !rowArchived.get(id)),
            onClear: clearSelection,
            onArchive: handleBulkArchive,
            onUnarchive: handleBulkUnarchive,
          })
        : renderMailFilterPanel({ ...panel, onRefresh: handleRefresh, cooldown, syncStatus })}

      <div class="flex-1 min-h-0 overflow-y-auto">
        ${MailOverview({
          filter,
          entityClass: EnrichedMailEntity as any,
          operation: subscribeEnrichedMailEntitiesPaginatedFiltered as any,
          includeDefaultColumns: false,
          additionalColumns: [makeMailColumn(props.selectedMailID, selectedIDs, handleSelect, registerRow)],
          heading: null,
        })}
      </div>
    </div>
  `;
});
